import React, { useEffect, useRef, useState } from 'react'

import { Row, Col, Image, Form, Input, Typography, Divider, Button, Modal, List, message } from 'antd'
import { VideoCameraOutlined, PlayCircleOutlined, StopOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import {
  endCourse,
  getOneCourse,
  updateCourse
} from '../../reducers/courseSettingsReducer'
import { useHistory, useParams } from 'react-router-dom'
import Spinner from '../../components/Spinner'
import * as liveSessionsService from '../../services/liveSessions'

import { CompactPicker } from 'react-color'

const { Title, Text } = Typography

const CourseSettings = (props) => {
  const dispatch = useDispatch()
  const { courseId } = useParams()
  const history = useHistory()

  const [courseImage, setCourseImage] = useState(null)
  const [liveSessions, setLiveSessions] = useState([])
  const [liveSessionsLoading, setLiveSessionsLoading] = useState(false)
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false)
  const [scheduleForm] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const [form] = Form.useForm()

  const course = useSelector((state) => state.courseSettings.data)
  const loading = useSelector((state) => state.courseSettings.loading)

  useEffect(() => {
    dispatch(getOneCourse(courseId))
  }, [])

  const loadLiveSessions = async () => {
    if (!courseId) return
    setLiveSessionsLoading(true)
    try {
      const list = await liveSessionsService.listByCourse(courseId)
      setLiveSessions(list || [])
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load live sessions')
    } finally {
      setLiveSessionsLoading(false)
    }
  }

  useEffect(() => {
    if (courseId) loadLiveSessions()
  }, [courseId])

  useEffect(() => {
    form.setFieldsValue({
      name: course.name,
      description: course.description,
      backgroundColor: course.backgroundColor,
      image: course.image
    })
    setCourseImage(course.image || null)
  }, [course])

  const handleSubmit = (values) => {
    dispatch(updateCourse(courseId, values))
  }

  const handleCourseEnd = () => {
    dispatch(endCourse(courseId)).then(() => history.push('/app/courses'))
  }

  if (loading) return <Spinner size="large" />

  const handleChangeImage = (event) => {
    console.log(event.target.value)
    setCourseImage(event.target.value)
  }

  const getColor = (event) => {
    console.log(event)
    return event.hex
  }

  const handleScheduleSubmit = async (values) => {
    const { title, description, date, time, duration } = values
    const scheduledAt = new Date(`${date} ${time}`)
    if (isNaN(scheduledAt.getTime())) {
      message.error('Invalid date or time')
      return
    }
    setSubmitting(true)
    try {
      await liveSessionsService.create({
        courseId,
        title,
        description: description || '',
        scheduledAt: scheduledAt.toISOString(),
        duration: Number(duration) || 60
      })
      message.success('Live session scheduled. Students have been notified.')
      setScheduleModalVisible(false)
      scheduleForm.resetFields()
      loadLiveSessions()
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to schedule session')
    } finally {
      setSubmitting(false)
    }
  }

  const handleStartSession = async (session) => {
    try {
      const id = session.id || session._id
      await liveSessionsService.start(id)
      message.success('Session is now live.')
      loadLiveSessions()
      window.open(
        `${window.location.origin}/app/course/${courseId}/live/${id}`,
        '_blank',
        'noopener,noreferrer'
      )
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to start session')
    }
  }

  const handleCancelSession = async (session) => {
    try {
      await liveSessionsService.cancel(session.id || session._id)
      message.success('Session cancelled.')
      loadLiveSessions()
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to cancel session')
    }
  }

  const handleCompleteSession = async (session) => {
    try {
      await liveSessionsService.complete(session.id || session._id)
      message.success('Session ended.')
      loadLiveSessions()
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to end session')
    }
  }

  const now = new Date()
  const upcomingSessions = liveSessions.filter(
    (s) => s.status === 'scheduled' || s.status === 'live'
  )

  return (
    <Form onFinish={handleSubmit} form={form} layout="vertical">
      <Row gutter={[48, 16]}>
        <Col span={8}>
          <Image placeholder={true} width={530} src={courseImage} />
          <Form.Item
            label={
              <Title style={{ marginBottom: '0', marginTop: '8px' }} level={5}>
                Course Image
              </Title>
            }
            name="image"
            onChange={handleChangeImage}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={16}>
          <div
            style={{
              backgroundColor: '#fafafa',
              height: 'auto',
              borderRadius: '10px',
              padding: '36px'
            }}
          >
            <Title level={3} style={{ marginBottom: '24px' }}>
              Settings
            </Title>
            <Divider />
            <Form.Item wrapperCol={{ span: 8 }} label="Course Name" name="name">
              <Input placeholder="Course Name" />
            </Form.Item>
            <Form.Item
              wrapperCol={{ span: 12 }}
              label="Course Description"
              name="description"
            >
              <Input.TextArea
                autoSize={{ minRows: 3, maxRows: 6 }}
                placeholder="Course Description"
                allowClear
              />
            </Form.Item>
            <Form.Item
              getValueFromEvent={getColor}
              label="Background Color"
              name="backgroundColor"
              valuePropName="color"
            >
              <CompactPicker />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Form.Item>
            <Divider />
            <Title level={4} style={{ marginBottom: 16 }}>
              Live Sessions
            </Title>
            <Button
              type="primary"
              icon={<VideoCameraOutlined />}
              onClick={() => setScheduleModalVisible(true)}
              style={{ marginBottom: 16 }}
            >
              Schedule Live Session
            </Button>
            <List
              loading={liveSessionsLoading}
              dataSource={upcomingSessions}
                renderItem={(session) => {
                  const scheduledAt = new Date(session.scheduledAt)
                  const joinableFrom = new Date(scheduledAt.getTime() - 30 * 60 * 1000)
                  const canStart =
                    session.status === 'scheduled' && now >= joinableFrom
                  const sid = session.id || session._id
                  return (
                    <List.Item
                      key={sid}
                      actions={[
                        session.status === 'live' && (
                          <Button
                            key="join"
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={() =>
                              window.open(
                              `${window.location.origin}/app/course/${courseId}/live/${sid}`,
                              '_blank',
                              'noopener,noreferrer'
                            )
                            }
                          >
                            Join Meeting
                          </Button>
                        ),
                        canStart && (
                          <Button
                            key="start"
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={() => handleStartSession(session)}
                          >
                            Start Session
                        </Button>
                      ),
                      session.status === 'scheduled' && (
                        <Button
                          key="cancel"
                          danger
                          icon={<StopOutlined />}
                          onClick={() => handleCancelSession(session)}
                        >
                          Cancel
                        </Button>
                      ),
                      session.status === 'live' && (
                        <Button
                          key="end"
                          danger
                          icon={<StopOutlined />}
                          onClick={() => handleCompleteSession(session)}
                        >
                          End Session
                        </Button>
                      )
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      title={
                        <span>
                          {session.title}
                          {session.status === 'live' && (
                            <Text type="danger" style={{ marginLeft: 8 }}>
                              Live
                            </Text>
                          )}
                        </span>
                      }
                      description={
                        <>
                          {scheduledAt.toLocaleString()} · {session.duration} min
                          {session.description && ` · ${session.description}`}
                        </>
                      }
                    />
                  </List.Item>
                )
              }}
            />
            <Divider />
            <Title
              style={{ color: 'firebrick', marginBottom: '24px' }}
              level={3}
            >
              Finalize Course
            </Title>

            <Text strong>
              If you end the course the current grades will be saved and the
              course will be archived
            </Text>
            <div style={{ marginTop: '16px' }}>
              <Button type="primary" danger onClick={handleCourseEnd}>
                End Course
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Modal
        title="Schedule Live Session"
        visible={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false)
          scheduleForm.resetFields()
        }}
        footer={null}
      >
        <Form
          form={scheduleForm}
          layout="vertical"
          onFinish={handleScheduleSubmit}
          initialValues={{ duration: 60 }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Enter a title' }]}
          >
            <Input placeholder="e.g. Week 3 Q&A" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true }]}
          >
            <Input type="date" />
          </Form.Item>
          <Form.Item
            name="time"
            label="Start Time"
            rules={[{ required: true }]}
          >
            <Input type="time" />
          </Form.Item>
          <Form.Item
            name="duration"
            label="Duration (minutes)"
            rules={[{ required: true }]}
          >
            <Input type="number" min={5} max={480} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Schedule & Notify Students
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  )
}

export default CourseSettings
